import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import { deriveCohortId } from '../utils/crypto';
import { getCohortHistory, addToCohortHistory } from '../utils/cohortHistory';
import type { CohortHistoryEntry } from '../utils/cohortHistory';
import { CopyableAddress } from '../components/CopyableAddress';
import { RpcSettings } from '../components/RpcSettings';

export function MainPage() {
  const navigate = useNavigate();
  const { passkeyAddress, logout } = useAuth();
  const [cohortTitle, setCohortTitle] = useState('');
  const [cohortHistory, setCohortHistory] = useState<CohortHistoryEntry[]>([]);

  // Load history on mount
  useEffect(() => {
    setCohortHistory(getCohortHistory());
  }, []);

  const trimmedTitle = cohortTitle.trim();
  const potentialCohortId = trimmedTitle ? deriveCohortId(trimmedTitle) : null;

  const handleLoadCohort = () => {
    if (potentialCohortId && trimmedTitle) {
      addToCohortHistory(trimmedTitle, potentialCohortId);
      navigate(`/${potentialCohortId}`);
    }
  };

  return (
    <div className="container">
      <div className="cohort-landing">
        {/* Hero section */}
        <div className="cohort-hero">
          <div className="hero-glow"></div>
          <h2 className="hero-title">Track contributions together</h2>
          <p className="hero-subtitle">
            Create a cohort to record sessions, track participation, and distribute rewards fairly.
          </p>
        </div>

        {/* Main input card */}
        <div className="cohort-input-card">
          <div className="cohort-input-wrapper">
            <input
              type="text"
              value={cohortTitle}
              onChange={(e) => setCohortTitle(e.target.value)}
              placeholder="Name your cohort..."
              className="cohort-name-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && trimmedTitle) {
                  handleLoadCohort();
                }
              }}
            />
            <button
              onClick={handleLoadCohort}
              className={`cohort-go-btn ${trimmedTitle ? 'active' : ''}`}
              disabled={!trimmedTitle}
            >
              {trimmedTitle ? 'Go' : '...'}
            </button>
          </div>
          {potentialCohortId && (
            <p className="cohort-id-preview">
              ID: {potentialCohortId.slice(0, 10)}...{potentialCohortId.slice(-8)}
            </p>
          )}
        </div>

        {/* Recent cohorts */}
        {cohortHistory.length > 0 && (
          <div className="recent-cohorts">
            <p className="recent-cohorts-label">Recent cohorts</p>
            <div className="recent-cohorts-list">
              {cohortHistory.slice(0, 5).map((entry) => (
                <button
                  key={entry.cohortId}
                  className="recent-cohort-chip"
                  onClick={() => {
                    addToCohortHistory(entry.title, entry.cohortId as `0x${string}`);
                    navigate(`/${entry.cohortId}`);
                  }}
                >
                  {entry.title}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Footer with account info */}
        <div className="cohort-landing-footer">
          {passkeyAddress ? (
            <>
              <CopyableAddress address={passkeyAddress} />
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  logout();
                  navigate('/auth');
                }}
                className="logout-link"
              >
                Switch account
              </a>
            </>
          ) : (
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                navigate('/auth');
              }}
              className="logout-link"
            >
              Sign in with passkey
            </a>
          )}
          <RpcSettings />
        </div>
      </div>
    </div>
  );
}
